/**
 * React hook for Team Chat WebSocket connection.
 * Handles real-time updates, typing indicators, and presence.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import chatWebsocketService from '../services/chatWebsocket';
import { chatKeys } from './useChat';
import { playNotificationSound } from '../utils/notificationSound';
import type {
  Message,
  TypingPayload,
  PresencePayload,
  MessageDeletedPayload,
} from '../types/chat';

interface UseChatWebSocketOptions {
  organizationId?: string;
  autoConnect?: boolean;
  currentUserId?: string | number;
  activeChannelId?: string | null;
  mutedChannelIds?: Set<string>;
  onMessage?: (message: Message) => void;
  onTyping?: (typing: TypingPayload) => void;
  onPresence?: (presence: PresencePayload) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: unknown) => void;
}

interface UseChatWebSocketReturn {
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  joinChannel: (channelId: string) => void;
  leaveChannel: (channelId: string) => void;
  startTyping: (channelId: string) => void;
  stopTyping: (channelId: string) => void;
  markRead: (channelId: string, messageId?: string) => void;
  typingUsers: Map<string, TypingPayload[]>;
  onlineUsers: Set<string>;
}

export const useChatWebSocket = (
  options: UseChatWebSocketOptions = {}
): UseChatWebSocketReturn => {
  const {
    organizationId,
    autoConnect = true,
    currentUserId,
    activeChannelId,
    mutedChannelIds,
    onMessage,
    onTyping,
    onPresence,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const [isConnected, setIsConnected] = useState(chatWebsocketService.isConnected());
  const [typingUsers, setTypingUsers] = useState<Map<string, TypingPayload[]>>(
    new Map()
  );
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const typingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    // Connection handlers
    const handleConnected = () => {
      setIsConnected(true);
      onConnect?.();
    };

    const handleDisconnected = () => {
      setIsConnected(false);
      onDisconnect?.();
    };

    const handleError = (error: unknown) => {
      onError?.(error);
    };

    // Message handlers
    const handleNewMessage = (data: unknown) => {
      const message = data as Message;
      onMessage?.(message);

      // Note: Notification sounds are NOT played here for regular messages.
      // Sounds are only played for:
      // - DMs (via handleDMNotification)
      // - @mentions (via handleMentionNotification)

      // Check if this is a thread reply (has parent_message)
      if (message.parent_message) {
        // Add to thread cache instead of main channel
        queryClient.setQueryData(
          chatKeys.thread(message.parent_message),
          (old: Message[] | undefined) => {
            if (!old) return [message];
            // Check if message already exists
            if (old.some((m) => m.id === message.id)) {
              return old;
            }
            // Add to the end (chronological order for threads)
            return [...old, message];
          }
        );

        // Invalidate channel messages to get fresh reply_count from server
        // This ensures the parent message has accurate reply counts
        queryClient.invalidateQueries({
          queryKey: chatKeys.channelMessages(message.channel),
        });
      } else {
        // Regular message - add to channel messages cache (infinite query structure)
        queryClient.setQueryData(
          chatKeys.channelMessages(message.channel),
          (old: { pages: Array<{ results: Message[]; next: string | null; previous: string | null }>; pageParams: (string | null)[] } | undefined) => {
            if (!old || !old.pages?.length) {
              return {
                pages: [{ results: [message], next: null, previous: null }],
                pageParams: [null],
              };
            }
            // Check if message already exists in first page
            if (old.pages[0].results.some((m) => m.id === message.id)) {
              return old;
            }
            // Add to the beginning of first page (newest messages)
            return {
              ...old,
              pages: [
                { ...old.pages[0], results: [message, ...old.pages[0].results] },
                ...old.pages.slice(1),
              ],
            };
          }
        );
      }

      // Force immediate refetch of unread counts
      queryClient.refetchQueries({ queryKey: chatKeys.unread() });
    };

    const handleMessageUpdated = (data: unknown) => {
      const message = data as Message;
      queryClient.setQueryData(
        chatKeys.channelMessages(message.channel),
        (old: { pages: Array<{ results: Message[]; next: string | null; previous: string | null }>; pageParams: (string | null)[] } | undefined) => {
          if (!old || !old.pages?.length) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((m) => (m.id === message.id ? message : m)),
            })),
          };
        }
      );
    };

    const handleMessageDeleted = (data: unknown) => {
      const payload = data as MessageDeletedPayload;
      queryClient.setQueryData(
        chatKeys.channelMessages(payload.channel_id),
        (old: { pages: Array<{ results: Message[]; next: string | null; previous: string | null }>; pageParams: (string | null)[] } | undefined) => {
          if (!old || !old.pages?.length) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.filter((m) => m.id !== payload.message_id),
            })),
          };
        }
      );
    };

    // Typing handler
    const handleTyping = (data: unknown) => {
      const payload = data as TypingPayload;
      onTyping?.(payload);

      const key = `${payload.channel_id}-${payload.user_id}`;

      // Clear existing timeout for this user
      const existingTimeout = typingTimeoutsRef.current.get(key);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      if (payload.is_typing) {
        // Add user to typing list
        setTypingUsers((prev) => {
          const newMap = new Map(prev);
          const channelTyping = newMap.get(payload.channel_id) || [];
          const filtered = channelTyping.filter(
            (t) => t.user_id !== payload.user_id
          );
          newMap.set(payload.channel_id, [...filtered, payload]);
          return newMap;
        });

        // Auto-remove after 5 seconds
        const timeout = setTimeout(() => {
          setTypingUsers((prev) => {
            const newMap = new Map(prev);
            const channelTyping = newMap.get(payload.channel_id) || [];
            newMap.set(
              payload.channel_id,
              channelTyping.filter((t) => t.user_id !== payload.user_id)
            );
            return newMap;
          });
          typingTimeoutsRef.current.delete(key);
        }, 5000);

        typingTimeoutsRef.current.set(key, timeout);
      } else {
        // Remove user from typing list
        setTypingUsers((prev) => {
          const newMap = new Map(prev);
          const channelTyping = newMap.get(payload.channel_id) || [];
          newMap.set(
            payload.channel_id,
            channelTyping.filter((t) => t.user_id !== payload.user_id)
          );
          return newMap;
        });
      }
    };

    // Presence handler
    const handlePresence = (data: unknown) => {
      const payload = data as PresencePayload;
      onPresence?.(payload);

      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        if (payload.is_online) {
          newSet.add(payload.user_id);
        } else {
          newSet.delete(payload.user_id);
        }
        return newSet;
      });
    };

    // Reaction handler
    const handleReactionUpdate = (data: unknown) => {
      // Invalidate messages for the channel to refresh reactions
      // We could be more granular here, but this keeps it simple
      queryClient.invalidateQueries({ queryKey: chatKeys.all });
    };

    // DM notification handler - play sound for new DMs
    const handleDMNotification = (data: unknown) => {
      const payload = data as { message: Message; channel_id: string };
      const isFromOtherUser = currentUserId && payload.message?.sender?.id !== String(currentUserId);
      const isViewingChannel = activeChannelId && payload.channel_id === activeChannelId;
      const isChannelMuted = mutedChannelIds?.has(payload.channel_id);

      // Play sound for DMs when not viewing the channel and not muted
      if (isFromOtherUser && !isViewingChannel && !isChannelMuted) {
        playNotificationSound();
      }
    };

    // Mention notification handler - play sound when @mentioned
    const handleMentionNotification = (data: unknown) => {
      const payload = data as { message: Message; channel_id: string };
      const isFromOtherUser = currentUserId && payload.message?.sender?.id !== String(currentUserId);
      const isViewingChannel = activeChannelId && payload.channel_id === activeChannelId;
      const isChannelMuted = mutedChannelIds?.has(payload.channel_id);

      // Play sound for @mentions when not viewing the channel and not muted
      if (isFromOtherUser && !isViewingChannel && !isChannelMuted) {
        playNotificationSound();
      }
    };

    // Register handlers
    chatWebsocketService.on('connected', handleConnected);
    chatWebsocketService.on('disconnected', handleDisconnected);
    chatWebsocketService.on('error', handleError);
    chatWebsocketService.on('new_message', handleNewMessage);
    chatWebsocketService.on('message_updated', handleMessageUpdated);
    chatWebsocketService.on('message_deleted', handleMessageDeleted);
    chatWebsocketService.on('typing', handleTyping);
    chatWebsocketService.on('presence', handlePresence);
    chatWebsocketService.on('reaction_update', handleReactionUpdate);
    chatWebsocketService.on('dm_notification', handleDMNotification);
    chatWebsocketService.on('mention_notification', handleMentionNotification);

    // Auto-connect
    if (autoConnect && !chatWebsocketService.isConnected()) {
      chatWebsocketService.connect(organizationId);
    }

    // Cleanup
    return () => {
      chatWebsocketService.off('connected', handleConnected);
      chatWebsocketService.off('disconnected', handleDisconnected);
      chatWebsocketService.off('error', handleError);
      chatWebsocketService.off('new_message', handleNewMessage);
      chatWebsocketService.off('message_updated', handleMessageUpdated);
      chatWebsocketService.off('message_deleted', handleMessageDeleted);
      chatWebsocketService.off('typing', handleTyping);
      chatWebsocketService.off('presence', handlePresence);
      chatWebsocketService.off('reaction_update', handleReactionUpdate);
      chatWebsocketService.off('dm_notification', handleDMNotification);
      chatWebsocketService.off('mention_notification', handleMentionNotification);

      // Clear typing timeouts
      typingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      typingTimeoutsRef.current.clear();
    };
  }, [
    organizationId,
    autoConnect,
    currentUserId,
    activeChannelId,
    mutedChannelIds,
    onMessage,
    onTyping,
    onPresence,
    onConnect,
    onDisconnect,
    onError,
    queryClient,
  ]);

  const connect = useCallback(() => {
    chatWebsocketService.connect(organizationId);
  }, [organizationId]);

  const disconnect = useCallback(() => {
    chatWebsocketService.disconnect();
  }, []);

  const joinChannel = useCallback((channelId: string) => {
    chatWebsocketService.joinChannel(channelId);
  }, []);

  const leaveChannel = useCallback((channelId: string) => {
    chatWebsocketService.leaveChannel(channelId);
  }, []);

  // Debounced typing indicator
  const typingDebounceRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const startTyping = useCallback((channelId: string) => {
    // Clear existing debounce
    const existing = typingDebounceRef.current.get(channelId);
    if (existing) {
      clearTimeout(existing);
    }

    chatWebsocketService.startTyping(channelId);

    // Auto-stop after 3 seconds of no typing
    const timeout = setTimeout(() => {
      chatWebsocketService.stopTyping(channelId);
      typingDebounceRef.current.delete(channelId);
    }, 3000);

    typingDebounceRef.current.set(channelId, timeout);
  }, []);

  const stopTyping = useCallback((channelId: string) => {
    const existing = typingDebounceRef.current.get(channelId);
    if (existing) {
      clearTimeout(existing);
      typingDebounceRef.current.delete(channelId);
    }
    chatWebsocketService.stopTyping(channelId);
  }, []);

  const markRead = useCallback((channelId: string, messageId?: string) => {
    chatWebsocketService.markRead(channelId, messageId);
  }, []);

  return {
    isConnected,
    connect,
    disconnect,
    joinChannel,
    leaveChannel,
    startTyping,
    stopTyping,
    markRead,
    typingUsers,
    onlineUsers,
  };
};

export default useChatWebSocket;

/**
 * Hook to get typing users for a specific channel.
 */
export const useChannelTyping = (
  channelId: string | null,
  typingUsers: Map<string, TypingPayload[]>
): TypingPayload[] => {
  if (!channelId) return [];
  return typingUsers.get(channelId) || [];
};

/**
 * Hook to check if a user is online.
 */
export const useIsUserOnline = (
  userId: string | null,
  onlineUsers: Set<string>
): boolean => {
  if (!userId) return false;
  return onlineUsers.has(userId);
};
